import { OneWayDragger } from "./OneWayDragger";
import type { DrawingModule } from "../types/DrawingWeb";
import { DraggerType, type Point2D, type Point3D } from "./types";
import { deleteObj } from "../core/delete";
import { openAs, getEntityHandle, getCurrentSpaceId } from "../core/oda";
import { OpenMode } from "../core/constants";
import { AddEntityCommand } from "../commands/AddEntityCommand";
import { getDwgBlockId, appendAttributesFromDefinitions } from "../blocks";
import { setEntityLayer } from "../entities";
import { PREVIEW_BLOCK_STYLE } from "./previewStyles";
import { trackTool } from "@/analytics/drawing-analytics";

export class BlockDragger extends OneWayDragger {
  readonly type = DraggerType.Block;

  /** Name of the active block — must already exist in the drawing database */
  private _activeBlockName: string | null = null;

  /** Scale factor for block inserts */
  private _blockScale = 1.0;

  /** Rotation angle in radians */
  private _blockRotation = 0;

  /**
   * Attribute values applied to the next inserts, keyed by tag — filled by the
   * pre-insert form of the blocks drawer. Tags absent from the map (or a null map)
   * fall back to the definition's default text.
   */
  private _attributeValues: Record<string, string> | null = null;

  private previewBlockId: string | null = null;

  // ============== Configuration ==============

  get activeBlockName(): string | null {
    return this._activeBlockName;
  }

  set activeBlockName(name: string | null) {
    if (name !== this._activeBlockName) {
      // Values entered for one block must never leak onto another.
      this._attributeValues = null;
    }
    this._activeBlockName = name;
  }

  get attributeValues(): Record<string, string> | null {
    return this._attributeValues;
  }

  set attributeValues(values: Record<string, string> | null) {
    this._attributeValues = values;
  }

  get blockScale(): number {
    return this._blockScale;
  }

  set blockScale(scale: number) {
    this._blockScale = Math.max(0.01, scale);
  }

  get blockRotation(): number {
    return this._blockRotation;
  }

  set blockRotation(radians: number) {
    this._blockRotation = radians;
  }

  // ============== Preview ==============

  protected createPreview(screen: Point2D): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    this.previewBlockId = overlay.createPoint(
      screen.x,
      screen.y,
      8,
      PREVIEW_BLOCK_STYLE,
    );

    if (this.previewBlockId) {
      this.addPreviewId(this.previewBlockId);
    }
  }

  protected updatePreview(screen: Point2D): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay || !this.previewBlockId) return;

    overlay.update(this.previewBlockId, {
      x: screen.x,
      y: screen.y,
    });
  }

  // ============== Entity Creation ==============

  protected createEntity(_screen: Point2D, world: Point3D): void {
    const module = this.module;
    const db = this.viewer.database;

    if (!db) {
      console.error("[BlockDragger] No database available");
      return;
    }

    if (!this._activeBlockName) {
      console.error("[BlockDragger] No active block set");
      return;
    }

    const blockRecordId = getDwgBlockId(this.viewer, this._activeBlockName);
    if (!blockRecordId || blockRecordId.isNull()) {
      console.error(
        `[BlockDragger] Block "${this._activeBlockName}" not found in database`,
      );
      return;
    }

    // Create block reference (insert)
    const blockRef = module.OdDbBlockReference.createObject();
    blockRef.setDatabaseDefaults(db, true);

    const noteConfigLayername = this.viewer.activeNoteConfig?.props?.layername;
    if (noteConfigLayername) {
      setEntityLayer(
        module,
        db,
        blockRef as unknown as DrawingModule.OdDbEntity,
        noteConfigLayername,
      );
    }

    blockRef.setBlockTableRecord(blockRecordId);

    const position = new module.OdGePoint3d(world.x, world.y, world.z);
    blockRef.setPosition(position);
    deleteObj(position);

    const scale = new module.OdGeScale3d(
      this._blockScale,
      this._blockScale,
      this._blockScale,
    );
    blockRef.setScaleFactors(scale);
    deleteObj(scale);

    if (this._blockRotation !== 0) {
      blockRef.setRotation(this._blockRotation);
    }

    const currentSpaceId = getCurrentSpaceId(db);
    const currentSpace = openAs(
      currentSpaceId,
      module.OdDbBlockTableRecord,
      OpenMode.kForWrite,
    );
    const entityId = currentSpace.appendOdDbEntity(blockRef);

    // Instantiate the definition's ATTDEFs on the reference — without this the
    // inserted block renders no attribute text and the attributes panel has
    // nothing to edit.
    const values = this._attributeValues;
    const attributeIds = appendAttributesFromDefinitions(
      module,
      blockRef,
      blockRecordId,
      { getValue: (tag, defaultValue) => values?.[tag] ?? defaultValue },
    );
    const componentHandles = attributeIds.map((id) => getEntityHandle(id));
    for (const id of attributeIds) deleteObj(id);

    deleteObj(currentSpace);
    deleteObj(blockRef);

    this.applyNoteConfigToEntity(entityId);

    const command = new AddEntityCommand(this.viewer, {
      entityHandle: getEntityHandle(entityId),
      originalLayerName: this.viewer.activeNoteConfig.props.layername ?? "0",
      componentHandles,
    });
    this.viewer.executeCommand(command);

    // Tracked here rather than where the block is picked in the drawer: picking
    // a block only arms the tool, and a block armed but never placed is not a
    // usage. This is the point where one actually lands in the drawing.
    trackTool("block_inserted", "canvas", { blockName: this._activeBlockName });

    this.selectCreatedEntity(entityId);
    deleteObj(entityId);
  }

  // ============== Override Minimum Drag ==============

  protected hasMinimumDrag(): boolean {
    if (!this.startScreen || !this.currentScreen) {
      return false;
    }
    const dx = this.currentScreen.x - this.startScreen.x;
    const dy = this.currentScreen.y - this.startScreen.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance >= 2;
  }
}
